import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { History, Plus } from 'lucide-react';
import { Ellie } from '@/components/Ellie';
import { ConversationHistory } from '@/components/chat/ConversationHistory';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { shortid } from '@/services/id.service';
import elliePng from '@/assets/images/pliable/ellie.png';

/**
 * Ellie on a phone.
 *
 * The desktop layout puts conversation history in a permanent 1/4-width rail
 * next to the thread; there's no room for that here, and the thread is the
 * whole point. So history moves into a sheet behind a button, and the identity
 * header shrinks to a single row — a phone keyboard eats half the screen, and
 * every pixel above the composer is one the conversation doesn't get.
 */
export default function MobileEllie({
  conversationId,
  agentType,
  initialQuestion,
}: {
  conversationId?: string;
  agentType: string;
  initialQuestion?: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [historyOpen, setHistoryOpen] = useState(false);

  // Picking a thread navigates; close the sheet behind it so returning from the
  // conversation doesn't land back on the list.
  useEffect(() => {
    setHistoryOpen(false);
  }, [location.pathname]);

  return (
    <div className="h-full w-full flex flex-col">
      <header className="flex items-center gap-2 px-3 py-2 page-header-surface flex-shrink-0">
        <img src={elliePng} alt="" className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
        <h1 className="text-base font-semibold text-foreground flex-1 min-w-0 truncate">Ellie</h1>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Conversation history"
          onClick={() => setHistoryOpen(true)}
        >
          <History className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="New chat"
          onClick={() => navigate(`/ellie/conversation/${shortid()}`)}
        >
          <Plus className="h-5 w-5" />
        </Button>
      </header>

      <div className="flex-1 min-h-0 overflow-hidden">
        <Ellie
          showHistory={false}
          conversationId={conversationId}
          agentType={agentType as any}
          initialQuestion={initialQuestion}
        />
      </div>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-[85vw] sm:w-[85vw] p-0 flex flex-col">
          <SheetHeader className="px-4 py-3 border-b border-border text-left">
            <SheetTitle>Conversations</SheetTitle>
          </SheetHeader>
          {/* No onSelectThread: in-app the list navigates by itself. */}
          <ConversationHistory
            activeThreadId={conversationId}
            onNewChat={() => navigate(`/ellie/conversation/${shortid()}`)}
            agentType={agentType}
            agentTypes={agentType === 'ellie' ? ['ellie', 'analyst'] : undefined}
            allowAllChats
            hideHeader
            className="flex-1 min-h-0 bg-background"
          />
        </SheetContent>
      </Sheet>
    </div>
  );
}
